import { useCallback, useEffect, useRef } from "react";
import { useElements } from "@stripe/react-stripe-js";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import {
  resolveStripeElementsCustomerSessionClientSecret,
  resolveStripeElementsPaymentMethodTypes,
  resolveStripeElementsSetupFutureUsage,
} from "../../../StripeElements/helpers";
import {
  type StripeElements,
  type StripeElementsUpdateOptions,
  stripeElementsModes,
} from "../../../StripeElements/types";
import { useProcessSubmission } from "./useProcessSubmission";

type UpdateEndListener = () => void;

/**
 * Custom hook to handle the submission process for Stripe Payment Elements configured with Payment
 * Method Configurations (PMC).
 *
 * Problem:
 * Stripe Elements initialized with a `payment_method_configuration` (PMC) ID implicitly expect the
 * associated Payment Intent (PI) to have been created with `automatic_payment_methods` enabled.
 * However, in our flows involving Invoicing and Subscription Schedules, Stripe automatically generates
 * the underlying PI *without* this flag. Instead, these PIs are created with a specific list of
 * `payment_method_types` (historically defaulting to 'card', now using the specific method selected
 * by the user). Attempting `stripe.confirmPayment` using Elements configured via PMC against such a PI
 * leads to a confirmation failure due to this mode mismatch.
 *
 * Solution (The "Trick"):
 * We must temporarily reconfigure the Elements *before* confirmation:
 * - Remove the `payment_method_configuration`.
 * - Add the specific `payment_method_types` array matching the PI/SI.
 * - Add `setup_future_usage` if applicable (e.g., for card payments to be saved).
 *
 * 1. `onSubmit` is called: Instead of confirming payment immediately, it prepares options to temporarily
 *    update the Stripe Elements, removing `payment_method_configuration` and adding the specific
 *    `payment_method_types` and `setup_future_usage` needed for the current method.
 * 2. Event Listener: Before updating, it attaches a one-time listener (`handleUpdateEnd`) to the `update-end` event.
 * 3. Update Triggered: It calls `elements.update()` with the temporary options.
 * 4. Submission Execution: When the `update-end` event fires (meaning Elements are ready with temporary config),
 *    `handleUpdateEnd` executes. It immediately removes itself as a listener to prevent multiple runs.
 *    It then calls `processSubmission()` (from the `useProcessSubmission` hook), which handles the actual
 *    call to the relevant Stripe SDK confirmation function (e.g., `stripe.confirmPayment`).
 * 5. Restoration: In a `finally` block within `handleUpdateEnd` (ensuring it runs after success or failure
 *    of `processSubmission`), it calls `elements.update()` again to restore the original
 *    `payment_method_configuration`. This allows the Payment Element to render correctly for potential retries.
 * 6. Cleanup: A `useEffect` ensures the listener is removed if the component unmounts prematurely.
 */
export const useOnSubmitWithPMC = () => {
  const {
    currentCommunityProcessorPaymentMethodConfigurationId:
      paymentMethodConfiguration,
    isLinkCapabilityActive,
    currentCommunityMemberPaymentProcessorSessionClientSecret,
    isMemberWithSavedPaymentMethods,
    isMemberUsingSavedPaymentMethod,
    stripePaymentMethodType: paymentMethodType,
    stripeElementsMode,
  } = usePaywallCheckoutContext();
  const elements = useElements() as StripeElements | null;

  // Use the hook to get the submission function
  const processSubmission = useProcessSubmission();

  // Ref to store the listener function so we can remove it
  const updateEndListenerRef = useRef<UpdateEndListener | null>(null);

  const onSubmit = useCallback(async () => {
    // Do nothing if elements are not ready
    if (!elements) {
      return;
    }

    // When in setup mode, we don't need to reconfigure the Elements.
    if (stripeElementsMode === stripeElementsModes.setup) {
      try {
        await processSubmission();
      } catch (error) {
        // `processSubmission` handles errors already, nothing to do here
      }
      return;
    }

    const handleUpdateEnd = async () => {
      // --- One-Time Execution Logic ---
      // 1. Do nothing if there is no listener (safeguard against multiple executions)
      if (!updateEndListenerRef.current) {
        return;
      }

      // 2. Immediately remove the listener to prevent multiple executions
      elements.off("update-end", updateEndListenerRef.current);
      updateEndListenerRef.current = null; // Clear the ref

      // 3. Execute the actual payment submission
      try {
        await processSubmission();
      } catch (error) {
        // `processSubmission` handles errors already, nothing to do here
      } finally {
        // Restore the options to their original state.
        const options: StripeElementsUpdateOptions = {
          customerSessionClientSecret:
            currentCommunityMemberPaymentProcessorSessionClientSecret,
          payment_method_configuration: paymentMethodConfiguration,
          payment_method_types: null,
          setup_future_usage: null,
        };
        elements.update(options);
      }
      // --- End One-Time Execution Logic ---
    };
    const updateEndListener: UpdateEndListener = () => {
      void handleUpdateEnd();
    };

    // --- One-Time Execution Logic ---
    // Store the listener function in the ref *before* attaching it
    updateEndListenerRef.current = updateEndListener;

    // Configure Elements options for submission
    const payment_method_types = resolveStripeElementsPaymentMethodTypes(
      isLinkCapabilityActive,
      paymentMethodType,
    );
    const customerSessionClientSecret =
      resolveStripeElementsCustomerSessionClientSecret(
        currentCommunityMemberPaymentProcessorSessionClientSecret,
        paymentMethodType,
        isMemberWithSavedPaymentMethods,
        isMemberUsingSavedPaymentMethod,
      );
    const setup_future_usage =
      resolveStripeElementsSetupFutureUsage(paymentMethodType);

    const options: StripeElementsUpdateOptions = {
      customerSessionClientSecret,
      payment_method_configuration: null, // Temporarily disable PMC
      payment_method_types,
      setup_future_usage,
    };

    // Attach the listener *before* calling update
    elements.on("update-end", updateEndListener);

    // Trigger the update - the 'update-end' event will fire after this completes
    elements.update(options);
  }, [
    paymentMethodConfiguration,
    isLinkCapabilityActive,
    paymentMethodType,
    currentCommunityMemberPaymentProcessorSessionClientSecret,
    isMemberWithSavedPaymentMethods,
    isMemberUsingSavedPaymentMethod,
    stripeElementsMode,
    elements,
    processSubmission,
  ]);

  // Cleanup listener if component unmounts while processing
  useEffect(
    () => () => {
      if (elements && updateEndListenerRef.current) {
        elements.off("update-end", updateEndListenerRef.current);
      }
    },
    [elements],
  );

  return { onSubmit };
};
